import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ProductoApiService } from '../services/producto-api.service';
import { CategoriaProductoApiService } from '../services/categoria-producto-api.service';
import {
  ProductResponse,
  ProductRequest,
  CurrencyResponse,
} from '../models/producto.model';
import { ProductCategoryResponse } from '../models/categoria-producto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { BarcodeComponent } from './barcode.component';
import { ProductoAttachmentsComponent } from '../producto-attachments/producto-attachments.component';
import { ProductoTierPricesComponent } from '../producto-tier-prices/producto-tier-prices.component';
import { ProductoVariantAttributesComponent } from '../producto-variant-attributes/producto-variant-attributes.component';
import { ProductoVariantsComponent } from '../producto-variants/producto-variants.component';
import { ProductoPriceCurrenciesComponent } from '../producto-price-currencies/producto-price-currencies.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { finalize } from 'rxjs';
import { ProductoCurrencyRateApiService } from '../services/producto-currency-rate-api.service';

@Component({
  selector: 'app-producto-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BarcodeComponent,
    ProductoAttachmentsComponent,
    ProductoTierPricesComponent,
    ProductoVariantAttributesComponent,
    ProductoVariantsComponent,
    ProductoPriceCurrenciesComponent,
  ],
  templateUrl: './producto-form.component.html',
})
export class ProductoFormComponent implements OnInit {
  @Input() drawerMode = false;
  @Output() saved = new EventEmitter<ProductResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  productId: number | null = null;
  currentProduct: ProductResponse | null = null;

  categorias: ProductCategoryResponse[] = [];
  loadingCategorias = false;

  monedas: CurrencyResponse[] = [];
  loadingMonedas = false;

  businessId = 1;
  categoriasDropdownOpen = false;

  // Collapsible sub-entity sections (edit + page mode only)
  showAttachments = false;
  showVariants = false;
  showVariantAttributes = false;
  showTierPrices = false;
  showPriceCurrencies = false;

  minDatetimeLocal = '';
  newPromoStatus = 'No definido';

  productOrServiceType: 'product' | 'service' | '' = '';
  /**
   * Inc 53: selección de UI de dos niveles. OJO: 'resources' y 'consulting' NO son
   * valores de `type` — el backend valida `type` con
   * `@Pattern(^(inventory|no_inventory|service)$)` y guarda el segundo nivel en
   * `subtype` (CHECK: subtype IS NULL OR (type='service' AND subtype IN
   * ('resources','consulting'))). La conversión vive en `aplicarTipoSeleccionado()`.
   */
  productOrServiceSelected:
    | 'inventory'
    | 'no_inventory'
    | 'resources'
    | 'consulting'
    | '' = '';

  // Image upload state
  imageFileId: number | null = null;
  imagePreviewUrl: string | null = null;
  imageUploading = false;

  constructor(
    private fb: FormBuilder,
    private productApi: ProductoApiService,
    private categoriaApi: CategoriaProductoApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private rateApi: ProductoCurrencyRateApiService,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;
    this.updateMinDate();
    this.cargarCategorias();
    this.cargarMonedas();

    if (!this.drawerMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditing = true;
        this.productId = +id;
        this.cargarProducto(this.productId);
      } else {
        this.loadBusinessCurrency();
      }
    }
  }

  reset(): void {
    this.isEditing = false;
    this.productId = null;
    this.currentProduct = null;
    this.error = '';
    this.saving = false;
    this.initForm();
  }

  loadProducto(id: number): void {
    this.isEditing = true;
    this.productId = id;
    this.cargarProducto(id);
  }

  loadBusinessCurrency() {
    this.rateApi.getBaseCurrency().subscribe({
      next: (businessCurrencyDefault) => {
        this.form.patchValue({
          currencyId: businessCurrencyDefault?.currencyId,
        });
      },
      error: () => {
        this.toast.warning({
          title: 'Moneda',
          message: 'Error al obtener moneda predeterminada de la empresa',
        });
      },
    });
  }

  private cargarCategorias(): void {
    this.loadingCategorias = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.categoriaApi.searchProductCategory({ businessId }).subscribe({
      next: (data) => {
        this.categorias = data;
        this.loadingCategorias = false;
      },
      error: () => {
        this.loadingCategorias = false;
      },
    });
  }

  private cargarMonedas(): void {
    this.loadingMonedas = true;
    this.productApi.getCurrencies().subscribe({
      next: (data) => {
        this.monedas = data;
        this.loadingMonedas = false;
        const currencyId = this.form.get('currencyId')?.value;
        if (currencyId) {
          const currency = data.find((m) => m.id === currencyId);
          if (currency) {
            this.form
              .get('prefix')
              ?.setValue(currency.prefix, { emitEvent: false });
          }
        }
      },
      error: () => {
        this.loadingMonedas = false;
      },
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Básicos
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      sku: ['', Validators.maxLength(100)],
      ref: ['', Validators.maxLength(100)],
      skuPrefix: ['', Validators.maxLength(20)],
      // Inc 53: control SOLO de UI que alimentan las tarjetas de tipo; se traduce a
      // `type` + `subtype` en aplicarTipoSeleccionado(). No viaja al backend.
      typeSelection: ['', Validators.required],
      type: ['', Validators.required],
      subtype: [null],
      categoryIds: [[]],
      imageUrl: ['', Validators.maxLength(500)],

      // Precios y costos
      currencyId: [null, Validators.required],
      prefix: ['', Validators.maxLength(10)],
      price: [null, [Validators.required, Validators.min(0)]],
      cost: [null, Validators.min(0)],
      minPrice: [null, Validators.min(0)],

      // Impuestos y descuentos
      taxPercent: [null, Validators.min(0)],
      isTaxable: [false],
      maxDiscountPercent: [null, [Validators.min(0), Validators.max(100)]],

      // Producto físico
      unitOfMeasure: ['', Validators.maxLength(50)],
      isTrackable: [false],
      stockQuantity: [null, Validators.min(0)],
      minStockLevel: [null, Validators.min(0)],

      // Servicio
      durationHours: [null, Validators.min(0)],
      isRecurring: [false],
      billingCycle: ['one-time'],
      billingCondition: ['auto_renew'],
      fixedPaymentCount: [null, Validators.min(1)],

      // Precios escalonados
      usesTierPricing: [false],

      // Vigencia promocional (Incidentes #33 y #56)
      promoValidFrom: [null],
      promoValidUntil: [null],
      promoPrice: [null, Validators.min(0.01)],
      promoEnabled: [true],

      // Estado (Incidente #1): selector de 4 estados; por defecto Activo.
      isActive: [true],
      status: ['draft'],
    });

    // Inc 53: la tarjeta elegida decide `type` y `subtype`.
    this.form
      .get('typeSelection')
      ?.valueChanges.subscribe((sel: string) =>
        this.aplicarTipoSeleccionado(sel),
      );


    // La chapa de estado depende del precio promocional, no solo de las fechas.
    this.form
      .get('promoPrice')
      ?.valueChanges.subscribe(() => this.promoValidDateChange());

    this.form.get('type')?.valueChanges.subscribe((value: string) => {
      if (value !== 'service') {
        this.form.get('isRecurring')?.setValue(false);
        this.form.get('billingCycle')?.setValue('one-time');
      }
      if (value === 'service') {
        this.form.get('isTrackable')?.setValue(false);
      }
    });

    this.form.get('isRecurring')?.valueChanges.subscribe((value: boolean) => {
      if (!value) {
        this.form.get('billingCycle')?.setValue('one-time');
        this.form.get('billingCondition')?.setValue('auto_renew');
      }
    });

    this.form.get('currencyId')?.valueChanges.subscribe((id: number | null) => {
      const currency = this.monedas.find((m) => m.id === id);
      if (currency) {
        this.form
          .get('prefix')
          ?.setValue(currency.prefix, { emitEvent: false });
      } else {
        this.form.get('prefix')?.setValue('', { emitEvent: false });
      }
    });
  }

  /**
   * Inc 53: traduce la tarjeta seleccionada al par (`type`, `subtype`) que espera el
   * backend. 'resources' / 'consulting' son SUBTIPOS de type='service', nunca tipos.
   */
  private aplicarTipoSeleccionado(sel: string): void {
    const typeCtrl = this.form.get('type')!;
    const subtypeCtrl = this.form.get('subtype')!;
    if (sel === 'resources' || sel === 'consulting') {
      typeCtrl.setValue('service');
      subtypeCtrl.setValue(sel);
    } else if (sel === 'inventory' || sel === 'no_inventory') {
      typeCtrl.setValue(sel);
      subtypeCtrl.setValue(null);
    } else {
      typeCtrl.setValue('');
      subtypeCtrl.setValue(null);
    }
  }

  /** ¿Hay alguna fecha de vigencia promocional capturada? */
  get tieneVigenciaPromo(): boolean {
    return !!(
      this.form?.get('promoValidFrom')?.value ||
      this.form?.get('promoValidUntil')?.value
    );
  }

  /** Vigencia cargada pero sin precio promocional: promoción a medias. */
  get promoPriceRequiredMissing(): boolean {
    return this.tieneVigenciaPromo && this.promoPriceValue === null;
  }

  /**
   * Motivo por el que la promoción no se puede guardar, o null si está bien.
   * El backend rechaza los mismos cuatro casos con 400.
   */
  get errorPromocionMessage(): string | null {
    if (this.promoRangeInvalid) {
      return 'La fecha de fin de vigencia no puede ser anterior a la de inicio.';
    }
    if (this.promoPriceInvalid) {
      return 'El precio promocional debe ser mayor que 0 y menor que el precio de venta.';
    }
    if (this.promoDatesRequiredMissing) {
      return 'Para definir un precio promocional debes indicar la fecha de inicio y la de fin de vigencia.';
    }
    if (this.promoPriceRequiredMissing) {
      return 'Para definir una vigencia promocional debes indicar el precio promocional.';
    }
    return null;
  }

  /** Inc 56: ¿hay un precio promocional capturado? */
  get promoPriceValue(): number | null {
    const raw = this.form?.get('promoPrice')?.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }

  /**
   * Inc 56: el precio promocional debe ser MENOR que el precio de venta
   * (el backend responde 400 "El precio promocional (X) debe ser menor que el
   * precio de venta (Y)"); se valida también en cliente.
   */
  get promoPriceInvalid(): boolean {
    const promo = this.promoPriceValue;
    if (promo === null) return false;
    if (promo <= 0) return true;
    const price = Number(this.form?.get('price')?.value);
    if (isNaN(price)) return false;
    return promo >= price;
  }

  /** Inc 56: con precio promocional, ambas fechas son obligatorias. */
  get promoDatesRequiredMissing(): boolean {
    if (this.promoPriceValue === null) return false;
    return (
      !this.form?.get('promoValidFrom')?.value ||
      !this.form?.get('promoValidUntil')?.value
    );
  }

  cargarProducto(id: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.productApi.getProductById(id, businessId).subscribe({
      next: (product: ProductResponse) => {
        this.currentProduct = product;
        this.imagePreviewUrl = product.imageUrl || null;
        // Inc 56: `promoStatus` lo deriva el backend y es autoritativo; sólo si no
        // viene se cae a las banderas antiguas del Inc 33.
        this.newPromoStatus = product.promoStatus
          ? this.etiquetaPromoStatus(product.promoStatus)
          : product.promoExpired
            ? 'Expirada'
            : product.promoNotYetStarted
              ? 'Programada'
              : product.promoVigente
                ? 'Vigente'
                : 'No definido';
        // Inc 53: reconstruye las dos tarjetas en cascada desde type + subtype.
        this.productOrServiceType =
          product.type === 'service' ? 'service' : 'product';
        this.productOrServiceSelected =
          product.type === 'service'
            ? ((product.subtype as 'resources' | 'consulting') ?? '')
            : (product.type as 'inventory' | 'no_inventory');
        this.form.patchValue({
          name: product.name,
          description: product.description || '',
          sku: product.sku || '',
          ref: product.ref || '',
          skuPrefix: product.skuPrefix || '',
          typeSelection: this.productOrServiceSelected,
          type: product.type,
          subtype:
            product.type === 'service' ? (product.subtype ?? null) : null,
          // Inc 22: todas las categorías del producto (GET /api/products/{id} trae categoryIds).
          categoryIds:
            product.categoryIds && product.categoryIds.length > 0
              ? product.categoryIds
              : product.idCategory
                ? [product.idCategory]
                : [],
          imageUrl: product.imageUrl || '',
          currencyId: product.currencyId || null,
          prefix: product.prefix || '',
          price: product.price,
          cost: product.cost ?? null,
          minPrice: product.minPrice ?? null,
          taxPercent: product.taxPercent ?? null,
          isTaxable: product.isTaxable ?? false,
          maxDiscountPercent: product.maxDiscountPercent ?? null,
          unitOfMeasure: product.unitOfMeasure || '',
          isTrackable: product.isTrackable ?? false,
          stockQuantity: product.stockQuantity ?? null,
          minStockLevel: product.minStockLevel ?? null,
          durationHours: product.durationHours ?? null,
          isRecurring: product.isRecurring,
          billingCycle: product.billingCycle || 'one-time',
          billingCondition: product.billingCondition || 'auto_renew',
          fixedPaymentCount: product.fixedPaymentCount ?? null,
          usesTierPricing: product.usesTierPricing ?? false,
          promoValidFrom: product.promoValidFrom ?? null,
          promoValidUntil: product.promoValidUntil ?? null,
          promoPrice: product.promoPrice ?? null,
          promoEnabled: product.promoEnabled ?? true,
          isActive: product.isActive,
          status:
            product.status ?? (product.isActive ? 'active' : 'discontinued'),
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el producto';
        this.loading = false;
      },
    });
  }

  private updateMinDate(): void {
    this.minDatetimeLocal = this.toLocalDateInput(new Date());
  }

  private toLocalDateInput(d: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Producto' : 'Nueva Producto';
  }

  get isService(): boolean {
    return this.form.get('type')?.value === 'service';
  }

  get isProduct(): boolean {
    const t = this.form.get('type')?.value;
    return t === 'inventory' || t === 'no_inventory';
  }

  get isRecurringValue(): boolean {
    return this.form.get('isRecurring')?.value === true;
  }

  get isFixedPayments(): boolean {
    return this.form.get('billingCondition')?.value === 'fixed_payments';
  }

  get skuIsEmpty(): boolean {
    const sku = this.form.get('sku')?.value;
    return !sku || sku.trim() === '';
  }

  get selectedPrefix(): string {
    return this.form.get('prefix')?.value || '';
  }

  /**
   * Vigencia promocional inválida: la fecha de fin es anterior a la de inicio (Incidente #33).
   * Solo aplica cuando ambas fechas están presentes.
   */
  get promoRangeInvalid(): boolean {
    const from = this.form?.get('promoValidFrom')?.value;
    const until = this.form?.get('promoValidUntil')?.value;
    return !!from && !!until && new Date(until) < new Date(from);
  }

  cancelLastProductOrServiceSelection() {
    // Inc 53: limpiar la tarjeta debe limpiar también el par type/subtype derivado.
    this.form?.patchValue({
      typeSelection: '',
      type: '',
      subtype: null,
    });
    this.productOrServiceSelected = '';
  }

  /**
   * Inc 56: el backend deriva `promoStatus` con 4 valores exactos
   * ('inactive' | 'scheduled' | 'active' | 'expired'). Se traduce al vocabulario
   * que ya usa el getter `promoStatus` de esta pantalla.
   */
  private etiquetaPromoStatus(status: string): string {
    switch (status) {
      case 'active':
        return 'Vigente';
      case 'scheduled':
        return 'Programada';
      case 'expired':
        return 'Expirada';
      default:
        return 'No definido';
    }
  }

  async guardar(): Promise<void> {
    // El botón se deshabilita con `form.invalid`, así que estos guardas son los
    // que sí se alcanzan: sin toast el clic no hacía NADA visible.
    const errorPromo = this.errorPromocionMessage;
    if (errorPromo) {
      this.form.markAllAsTouched();
      this.toast.error({ title: 'Promoción', message: errorPromo });
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Validación de nombre duplicado SOLO en creación
    if (!this.isEditing) {
      const nombreIngresado = (this.form.get('name')?.value ?? '').trim();
      const duplicado =
        await this.buscarProductoConMismoNombre(nombreIngresado);

      if (duplicado) {
        await this.confirmDialog.alert({
          title: 'Nombre de producto duplicado',
          message: `Ya existe un producto llamado "${duplicado.name}" (SKU: ${duplicado.sku || 's/n'}). Por favor, usa un nombre diferente.`,
          variant: 'warning',
        });
        return;
      }
    }

    if (this.form.pristine) {
      this.toast.info({
        title: 'Producto',
        message: 'No se realizaron nuevos cambios en el producto',
      });
      this.router.navigate(['/productos']);
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const v = this.form.value;

    const selectedCurrency = this.monedas.find((m) => m.id === v.currencyId);

    const productRequest: ProductRequest = {
      name: v.name,
      description: v.description || undefined,
      sku: v.sku || undefined,
      ref: v.ref || undefined,
      skuPrefix: !v.sku && v.skuPrefix ? v.skuPrefix || undefined : undefined,
      price: v.price,
      type: v.type,
      // Inc 53: el subtipo solo aplica a servicios; para productos va null.
      subtype: v.type === 'service' ? v.subtype || null : null,
      idCategory: v.categoryIds?.[0] || undefined,
      businessId,
      currencyId: v.currencyId || undefined,
      currencyCode: selectedCurrency?.code || undefined,
      prefix: v.prefix || undefined,
      cost: v.cost ?? undefined,
      minPrice: v.minPrice ?? undefined,
      taxPercent: v.taxPercent ?? undefined,
      isTaxable: v.isTaxable,
      maxDiscountPercent: v.maxDiscountPercent ?? undefined,
      imageUrl: v.imageUrl || undefined,
      // Incidente #1: el backend deriva isActive del status (active => true).
      isActive: v.status ? v.status === 'active' : v.isActive,
      status: v.status || undefined,
      usesTierPricing: v.usesTierPricing,
      // Vigencia promocional (Incidentes #33 y #56)
      promoValidFrom: v.promoValidFrom || undefined,
      promoValidUntil: v.promoValidUntil || undefined,
      promoPrice: this.promoPriceValue ?? undefined,
      promoEnabled: v.promoEnabled !== false,
      // Producto físico
      unitOfMeasure:
        v.type !== 'service' ? v.unitOfMeasure || undefined : undefined,
      isTrackable: v.type !== 'service' ? v.isTrackable : undefined,
      stockQuantity:
        v.type !== 'service' && v.isTrackable
          ? (v.stockQuantity ?? undefined)
          : undefined,
      minStockLevel:
        v.type !== 'service' && v.isTrackable
          ? (v.minStockLevel ?? undefined)
          : undefined,
      // Servicio
      durationHours:
        v.type === 'service' ? (v.durationHours ?? undefined) : undefined,
      isRecurring: v.type === 'service' ? v.isRecurring : false,
      billingCycle:
        v.type === 'service' && v.isRecurring ? v.billingCycle : undefined,
      billingCondition:
        v.type === 'service' && v.isRecurring
          ? v.billingCondition || undefined
          : undefined,
      fixedPaymentCount:
        v.type === 'service' &&
        v.isRecurring &&
        v.billingCondition === 'fixed_payments'
          ? (v.fixedPaymentCount ?? undefined)
          : undefined,
    };

    const request$ =
      this.isEditing && this.productId
        ? this.productApi.updateProduct(
            this.productId,
            businessId,
            productRequest,
          )
        : this.productApi.createProduct(productRequest);

    const wasEditing = this.isEditing;
    const categoryIds: number[] = v.categoryIds || [];

    request$.subscribe({
      next: (product) => {
        // Inc 22: persiste las múltiples categorías vía PUT /api/products/{id}/categories.
        this.productApi
          .setProductCategories(product.id, businessId, categoryIds)
          .subscribe({
            next: () => this.finalizarGuardadoProducto(product, wasEditing),
            error: () => {
              // El producto sí se guardó; solo falló la asociación de categorías.
              this.finalizarGuardadoProducto(product, wasEditing);
            },
          });
      },
      error: (err) => {
        this.saving = false;
        this.error =
          err.error?.message ||
          (wasEditing
            ? 'Error al actualizar el producto'
            : 'Error al crear el producto');
        this.toast.error({
          title: 'Error',
          message: this.error,
        });
      },
    });
  }

  private finalizarGuardadoProducto(
    product: ProductResponse,
    wasEditing: boolean,
  ): void {
    this.saving = false;
    this.toast.success({
      title: wasEditing ? 'Producto actualizado' : 'Producto creado',
      message: wasEditing
        ? `Los cambios en "${product.name}" se guardaron correctamente.`
        : `El producto "${product.name}" se creó correctamente.`,
    });
    if (this.drawerMode) {
      this.saved.emit(product);
    } else {
      this.router.navigate(['/productos']);
    }
  }

  private buscarProductoConMismoNombre(
    nombre: string,
  ): Promise<ProductResponse | null> {
    return new Promise((resolve) => {
      if (!nombre) {
        resolve(null);
        return;
      }

      this.productApi
        .searchProduct({
          businessId: this.businessId,
          searchName: nombre,
          page: 0,
          size: 10,
        })
        .subscribe({
          next: (response) => {
            const nombreNormalizado = nombre.toLowerCase();
            const coincidencia = response.content.find(
              (p) => (p.name ?? '').trim().toLowerCase() === nombreNormalizado,
            );
            resolve(coincidencia ?? null);
          },
          error: () => {
            resolve(null);
          },
        });
    });
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/productos']);
    }
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';
    //Mensaje específico para moneda
    if (field === 'currencyId' && control.errors['required']) {
      return 'Debes seleccionar una moneda para el producto.';
    }
    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['maxlength'])
      return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
    if (control.errors['min'])
      return `El valor mínimo es ${control.errors['min'].min}`;
    if (control.errors['max'])
      return `El valor máximo es ${control.errors['max'].max}`;

    return 'Campo inválido';
  }

  //Codigo de barras
  get skuValue(): string {
    return this.form.get('sku')?.value || '';
  }

  //Descripcion
  get descriptionLength(): number {
    return this.form.get('description')?.value?.length ?? 0;
  }

  get disabledReason(): string {
    if (!this.form.invalid) return '';

    // Buscar el primer error y traducirlo a un motivo claro
    const errorMap: { [key: string]: string } = {
      name: 'el nombre',
      typeSelection: 'el tipo',
      type: 'el tipo',
      subtype: 'el subtipo de servicio',
      price: 'el precio de venta',
      description: 'la descripción',
      sku: 'el SKU',
      skuPrefix: 'el prefijo de SKU',
      fixedPaymentCount: 'el número de pagos',
    };

    for (const [field, label] of Object.entries(errorMap)) {
      const control = this.form.get(field);
      if (control?.invalid) {
        if (control.errors?.['required']) return `Falta completar ${label}.`;
        if (control.errors?.['maxlength']) {
          const max = control.errors['maxlength'].requiredLength;
          return `${label.charAt(0).toUpperCase() + label.slice(1)} supera el máximo de ${max} caracteres.`;
        }
        if (control.errors?.['min'])
          return `${label.charAt(0).toUpperCase() + label.slice(1)} no es válido.`;
        if (control.errors?.['max'])
          return `${label.charAt(0).toUpperCase() + label.slice(1)} no es válido.`;
      }
    }

    return 'Revisa los campos marcados en rojo.';
  }

  //
  get categoriasSeleccionadas(): ProductCategoryResponse[] {
    const ids: number[] = this.form.get('categoryIds')?.value || [];
    return this.categorias.filter((c) => ids.includes(c.id));
  }

  toggleCategoria(id: number): void {
    const control = this.form.get('categoryIds')!;
    const current: number[] = [...(control.value || [])];
    const idx = current.indexOf(id);
    if (idx === -1) {
      current.push(id);
    } else {
      current.splice(idx, 1);
    }
    control.setValue(current);
    control.markAsDirty();
  }

  isCategoriaSeleccionada(id: number): boolean {
    const ids: number[] = this.form.get('categoryIds')?.value || [];
    return ids.includes(id);
  }

  removerCategoria(id: number, event: MouseEvent): void {
    event.stopPropagation();
    this.toggleCategoria(id);
  }

  removeImage(): void {
    this.imageFileId = null;
    this.imagePreviewUrl = null;
    this.form.patchValue({ imageUrl: '' });
    this.form.markAsDirty();
    // En modo edición, persistir la eliminación del logo de inmediato.
    if (this.isEditing && this.productId) {
      this.persistLogoChange();
    }
  }

  loadImageUrl(event: Event) {
    const imageUrl = event.target as HTMLInputElement;
    this.imageFileId = null;
    this.imagePreviewUrl = imageUrl.value;
    this.form.patchValue({
      imageUrl: imageUrl.value,
    });
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error({
        title: 'Formato no válido',
        message: 'Selecciona una imagen (JPG, PNG, WEBP).',
      });
      return;
    }
    this.imageUploading = true;
    this.productApi.uploadProductImage(file).subscribe({
      next: (res) => {
        this.imageUploading = false;
        this.form.patchValue({ imageUrl: res.publicUrl || undefined });
        this.imageFileId = res.fileId;
        this.imagePreviewUrl = res.publicUrl || null;
        this.form.markAsDirty();
        input.value = '';
        // En modo edición, persistir el logo en la plantilla apenas se sube (sin esperar a "Guardar").
        if (this.isEditing && this.productId) {
          this.persistLogoChange();
        } else {
          this.imageUploading = false;
        }
        if (!res.publicUrl) {
          this.toast.error({
            title: 'Aviso',
            message: 'La imagen se subió pero no se obtuvo una URL pública.',
          });
        }
      },
      error: (err) => {
        this.imageUploading = false;
        this.toast.error({
          title: 'Error al subir',
          message: err.error?.message || 'No se pudo subir la imagen.',
        });
      },
    });
  }

  private persistLogoChange(): void {
    const p = this.currentProduct;
    if (!this.isEditing || !this.productId || !p) {
      this.imageUploading = false;
      return;
    }
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const request: ProductRequest = {
      name: p.name,
      description: p.description || undefined,
      sku: p.sku || undefined,
      ref: p.ref || undefined,
      skuPrefix: !p.sku && p.skuPrefix ? p.skuPrefix || undefined : undefined,
      price: p.price,
      type: p.type,
      // Inc 53: este PUT reemplaza la entidad completa; omitir subtype lo nulificaría.
      subtype: p.type === 'service' ? (p.subtype ?? null) : null,
      idCategory: p.categoryIds?.[0] || undefined,
      businessId,
      currencyId: p.currencyId || undefined,
      currencyCode: p.currencyCode || undefined,
      prefix: p.prefix || undefined,
      cost: p.cost ?? undefined,
      minPrice: p.minPrice ?? undefined,
      taxPercent: p.taxPercent ?? undefined,
      isTaxable: p.isTaxable,
      maxDiscountPercent: p.maxDiscountPercent ?? undefined,
      imageUrl: p.imageUrl || undefined,
      isActive: p.status ? p.status === 'active' : p.isActive,
      status: p.status || undefined,
      usesTierPricing: p.usesTierPricing,
      promoValidFrom: p.promoValidFrom || undefined,
      promoValidUntil: p.promoValidUntil || undefined,
      // Inc 56: idem — sin estos dos el PUT borraría la promoción vigente.
      promoPrice: p.promoPrice ?? undefined,
      promoEnabled: p.promoEnabled ?? undefined,
      unitOfMeasure:
        p.type !== 'service' ? p.unitOfMeasure || undefined : undefined,
      isTrackable: p.type !== 'service' ? p.isTrackable : undefined,
      stockQuantity:
        p.type !== 'service' && p.isTrackable
          ? (p.stockQuantity ?? undefined)
          : undefined,
      minStockLevel:
        p.type !== 'service' && p.isTrackable
          ? (p.minStockLevel ?? undefined)
          : undefined,
      // Servicio
      durationHours:
        p.type === 'service' ? (p.durationHours ?? undefined) : undefined,
      isRecurring: p.type === 'service' ? p.isRecurring : false,
      billingCycle:
        p.type === 'service' && p.isRecurring ? p.billingCycle : undefined,
      billingCondition:
        p.type === 'service' && p.isRecurring
          ? p.billingCondition || undefined
          : undefined,
      fixedPaymentCount:
        p.type === 'service' &&
        p.isRecurring &&
        p.billingCondition === 'fixed_payments'
          ? (p.fixedPaymentCount ?? undefined)
          : undefined,
    };

    this.productApi
      .updateProduct(this.productId, businessId, request)
      .pipe(finalize(() => (this.imageUploading = false)))
      .subscribe({
        next: (updated) => {
          this.currentProduct = updated;
          this.toast.updated('Imagen de producto');
        },
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'No se pudo guardar el logo',
          });
        },
      });
  }

  get promoStatus() {
    switch (this.newPromoStatus) {
      case 'Vigente':
        return {
          text: 'Vigente',
          classes:
            'bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-300',
        };
      case 'Expirada':
        return {
          text: 'Expirada',
          classes:
            'bg-error-100 text-error-700 dark:bg-error-500/20 dark:text-error-300',
        };
      case 'Programada':
        return {
          text: 'Programada',
          classes:
            'bg-warning-100 text-warning-700 dark:bg-warning-500/20 dark:text-warning-300',
        };
      default:
        return {
          text: 'No definido',
          classes:
            'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        };
    }
  }

  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  promoValidDateChange(): void {
    if (!this.currentProduct) {
      return;
    }

    // Sin precio promocional no hay promoción que rotular (el backend devuelve
    // `promoStatus: 'inactive'` en ese caso).
    if (this.promoPriceValue === null) {
      this.newPromoStatus = 'No definido';
      return;
    }

    const from = this.parseLocalDate(this.form.get('promoValidFrom')?.value);
    const until = this.parseLocalDate(this.form.get('promoValidUntil')?.value);

    if (Number.isNaN(from.getTime()) || Number.isNaN(until.getTime())) {
      this.newPromoStatus = 'No definido';
      return;
    }

    from.setHours(0, 0, 0, 0);
    until.setHours(23, 59, 59, 999);

    const today = new Date();
    today.setHours(12, 0, 0, 0);

    if (today < from) {
      this.newPromoStatus = 'Programada';
    } else if (today > until) {
      this.newPromoStatus = 'Expirada';
    } else {
      this.newPromoStatus = 'Vigente';
    }
  }

  removePromotion(): void {
    // Quitar la promoción borra TODA la promoción: dejar el precio sin fechas
    // (o al revés) es justo el estado a medias que rompe el listado.
    this.form.patchValue({
      promoValidFrom: '',
      promoValidUntil: '',
      promoPrice: null,
    });
    this.form.markAsDirty();
    this.promoValidDateChange();
  }
}
